import { useState } from "react";
import LoadingSpinner from "../../../../sharedComponents/LoadingSpinner/LoadingSpinner";
import { Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select, Tooltip } from "@mui/material";
import { Info } from "@mui/icons-material";
import parseDuration from "../../../../sharedComponents/ParseDuration";
import { Link } from "react-router-dom";
import { DateRangePicker } from "rsuite";
import { de } from "date-fns/locale";
import { FaClock } from "react-icons/fa";

const DATE_OPTIONS = { day: '2-digit', month: '2-digit', year: 'numeric' };
const ABSENT_REASONS = ["Abwesend", "Urlaub", "Berufsschule", "Frei"];

function PlanningTable(props) {

    const [today] = useState(new Date());

    const isDatePickerDisabled = (employeeId, date) => {
        const rowState = props.absentState[`${employeeId}-${date}`];
        return rowState?.absent;
    };
    
    const handleCheckboxChange = (employeeId, date, scheduleId, event) => {
        const isChecked = event.target.checked;

        const dateString = date.toLocaleDateString('de-DE', DATE_OPTIONS)
    
        props.setAbsentState(prevState => ({
            ...prevState,
            [`${employeeId}-${dateString}`]: {
                absent: isChecked,
                comment: "Abwesend",
            }
        }));
        
        handleLocalChange(
            employeeId, 
            date.toLocaleDateString('de-DE', DATE_OPTIONS), 
            [new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0), new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 1)], 
            scheduleId, 
            isChecked,
            "Abwesend"
        );
    }

    const handleSelectChange = (employeeId, date, scheduleId, event) => {
        const newComment = event.target.value;

        const dateString = date.toLocaleDateString('de-DE', DATE_OPTIONS)

        props.setAbsentState(prevState => ({
            ...prevState,
            [`${employeeId}-${dateString}`]: {
                absent: props.absentState[`${employeeId}-${dateString}`]?.absent || false,
                comment: newComment,
            }
        }));

        handleLocalChange(
            employeeId, 
            date.toLocaleDateString('de-DE', DATE_OPTIONS), 
            [new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0), new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 1)], 
            scheduleId, 
            props.absentState[`${employeeId}-${dateString}`]?.absent || false,
            newComment
        );
    }

    const handleLocalChange = (employeeId, date, value, scheduleId, absent, comment) => {
        if (value === null) {
            if (scheduleId) {                
                props.setLocalChanges(prevState => ({
                    ...prevState,
                    [`${employeeId}-${date}`]: {
                        employee_id: employeeId,
                        date: date,
                        id: scheduleId,
                        toDelete: true,
                        comment: comment,
                        absent: absent,
                    }
                }))
            } else {
                props.setLocalChanges(prevState => {
                    const newState = { ...prevState };
                    delete newState[`${employeeId}-${date}`];
                    return newState;
                });
            }
        } else if (value[0] !== null && value[1] !== null) {
            const startTime = addZeroToTime(value[0].getHours()) + ":" + addZeroToTime(value[0].getMinutes());
            const endTime = addZeroToTime(value[1].getHours()) + ":" + addZeroToTime(value[1].getMinutes());

            if(scheduleId && absent === false && startTime === "00:00" && endTime === "00:01"){
                props.setLocalChanges(prevState => ({
                    ...prevState,
                    [`${employeeId}-${date}`]: {
                        employee_id: employeeId,
                        date: date,
                        id: scheduleId,
                        toDelete: true,
                        comment: comment,
                        absent: absent,
                    }
                }))
            }else if(!scheduleId && absent === false && startTime === "00:00" && endTime === "00:01"){
                props.setLocalChanges(prevState => {
                    const newState = { ...prevState };
                    delete newState[`${employeeId}-${date}`];
                    return newState;
                });
            } else {
                props.setLocalChanges(prevState => ({
                    ...prevState,
                    [`${employeeId}-${date}`]: {
                        employee_id: employeeId,
                        date: date,
                        start_time: startTime,
                        end_time: endTime,
                        id: scheduleId,
                        toDelete: false,
                        comment: comment,
                        absent: absent,
                    }
                }));
            }
        }
    }

    function addZeroToTime(time) {return time < 10 ? "0" + time : time;}

    function formatNegativeZero(value) {
        const roundedValue = Math.round(value);
        return roundedValue === 0 && value < 0 ? '-0' : roundedValue;
    }

    function tableFunction(dates, weeklyTotals) {
        let dynamicTable = [];
        let totalDurations = Array(7).fill(0);

        dynamicTable.push(
            <>
                <thead>
                    <tr>
                        <th></th>
                        {dates.map((date, index) => (
                            <th key={index} className={
                                date.getDate() === today.getDate() && 
                                date.getMonth() === today.getMonth() && 
                                date.getFullYear() === today.getFullYear()  
                                ? 'planning-table-today-header' : ''}
                            >
                                {["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"][date.getDay()]}
                                <br />
                                {date.toLocaleDateString('de-DE', DATE_OPTIONS)}
                            </th>
                        ))}
                        <th className="worktime-table-weeklytotal">Summe <Tooltip title="Summe der Wochenstunden mit Pausenzeit schon abgezogen" ><Info/></Tooltip> Wochenstunden</th>
                    </tr>
                </thead>
                <tbody>
                    {props.employees.map((employee, rowIndex) => {
                        if(props.selectedArea !== null && !props.selectedArea.assigned_employee_ids.includes(employee.id)){
                            return null;
                        }

                        const coreTime = props.employeesCoreWorkTime.find(coreTime => coreTime.id === employee.assigned_core_working_hours_policy_id);
                        
                        const targetTimeObject = props.targetHourTime.find(item => (employee.first_name === item.employee_first_name && employee.last_name === item.employee_last_name))
                        
                        let displayTime = "";
                        let targetTimeObjectMonth;
                        let objectInMinutes = 0;

                        if(targetTimeObject !== undefined) {
                            targetTimeObjectMonth = targetTimeObject.months?.find(object => object.month === props.currentDate.toISOString().slice(0, 7))
                            
                            if(targetTimeObjectMonth !== undefined && targetTimeObjectMonth.transfer_from_last_month !== null){
                                const targetTime = parseDuration(targetTimeObjectMonth.target_working_hours);
                                const targetAndTransferObject = {hours: targetTime.hours, minutes: targetTime.minutes}
                                
                                objectInMinutes = (targetAndTransferObject.hours * 60 + targetAndTransferObject.minutes) / 4.35;
                                displayTime = Math.floor(objectInMinutes/60) + " Std " + Math.floor(objectInMinutes % 60) + " min";
                            }
                        }

                        return (
                            <tr key={rowIndex}>
                                <th scope="row">
                                    {employee.first_name} {employee.last_name} 
                                    <br/>
                                    {employee.employment_type !== "---" && props.switchEmployeeFunction && ( 
                                        <>
                                            <Tooltip title="Beschäftigung">
                                                <i>{employee.employment_type}</i>
                                            </Tooltip>
                                            <br/>
                                        </>
                                    )}
                                    {coreTime && (
                                        <>
                                            <Link className="link-standardregelung" to={"/home/settings/coretime/detailView?id=" + employee.assigned_core_working_hours_policy_id} target="_blank">Standardregelung</Link>
                                            <br/>
                                        </>
                                    )}
                                    <div className="target-working-time-display">
                                        <Tooltip title="geplante wöchentliche Stunden">
                                            <span>{displayTime}</span>
                                        </Tooltip>                                        
                                    </div>                                    
                                </th>
                                {dates.map((currentDate, colIndex) => {
                                    let foundSchedule = false;
                                    let startTime = '';
                                    let endTime = '';

                                    const dateWeekDay = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"][currentDate.getDay()];

                                    if (coreTime) {
                                        const period = coreTime.core_working_hours_periods.find(period => period.day_of_week === dateWeekDay);
                                        if (period) {
                                            foundSchedule = true;
                                            startTime = period.start_time.slice(0, 5);
                                            endTime = period.end_time.slice(0, 5);
                                        }
                                    }
                                    
                                    const schedule = props.employeesSchedulingTime.find(schedule => 
                                        schedule.employee_id === employee.id && 
                                        new Date(schedule.date).toLocaleDateString('de-DE', DATE_OPTIONS) === currentDate.toLocaleDateString('de-DE', DATE_OPTIONS)
                                    );

                                    //!schedule.absent for not adding the duration of the saved absent objects (currently implemented with starttime 00:00 and endtime 00:01) 
                                    if (schedule && !schedule.absent) {
                                        foundSchedule = true;
                                        startTime = schedule.start_time.slice(0, 5);
                                        endTime = schedule.end_time.slice(0, 5);
                                    }

                                    if(coreTime && schedule && schedule.absent){
                                        startTime = "00:00"
                                        endTime = "00:00"
                                    }

                                    const localChange = props.localChanges[`${employee.id}-${currentDate.toLocaleDateString('de-DE', DATE_OPTIONS)}`];
                                    if (localChange?.toDelete) {
                                        return (
                                            <td key={colIndex}>
                                                <div className="deleted-label">Geplante Zeit entfernt</div>
                                            </td>
                                        );
                                    }

                                    const isAbsent = isDatePickerDisabled(employee.id, currentDate.toLocaleDateString('de-DE', DATE_OPTIONS));

                                    if (foundSchedule) {
                                        let initialStartDate = new Date(currentDate.setHours(parseInt(startTime.slice(0, 2)), parseInt(startTime.slice(3, 5)), 0, 0));
                                        let initialEndDate = new Date(currentDate.setHours(parseInt(endTime.slice(0, 2)), parseInt(endTime.slice(3, 5)), 0, 0));

                                        const duration = (initialEndDate - initialStartDate) / (1000 * 60); // calculate duration in minutes

                                        const durationMinusPause = duration - props.getAllTimePolicy[Math.min(Math.floor(duration / 60), 15)]

                                        totalDurations[colIndex] += durationMinusPause;

                                        return (
                                            <td key={colIndex}>
                                                <DateRangePicker 
                                                    defaultValue={isAbsent ? [] : [initialStartDate, initialEndDate]} 
                                                    format="HH:mm" 
                                                    character="-" 
                                                    placeholder={props.absentState[`${employee.id}-${currentDate.toLocaleDateString('de-DE', DATE_OPTIONS)}`]?.comment}
                                                    locale={de} 
                                                    caretAs={FaClock} 
                                                    oneTap 
                                                    onChange={value => handleLocalChange(employee.id, currentDate.toLocaleDateString('de-DE', DATE_OPTIONS), value, schedule?.id, false, "")}
                                                    cleanable={schedule !== undefined} 
                                                    disabled={isAbsent}
                                                />
                                                <div className="abwesend-checkbox">
                                                    <Tooltip title="Abwesendheit einstellen">
                                                        <FormControlLabel                                                             
                                                            control={                                                    
                                                                <Checkbox 
                                                                    defaultChecked={props.absentState[`${employee.id}-${currentDate.toLocaleDateString('de-DE', DATE_OPTIONS)}`]?.absent}
                                                                    onChange={(event) => handleCheckboxChange(employee.id, currentDate, schedule?.id, event)}
                                                            />} 
                                                            label="Abwesend"
                                                        />
                                                    </Tooltip>
                                                    {
                                                        props.absentState[`${employee.id}-${currentDate.toLocaleDateString('de-DE', DATE_OPTIONS)}`]?.absent && 
                                                        <FormControl fullWidth>
                                                            <InputLabel>Grund</InputLabel>
                                                            <Select 
                                                                label="Grund" 
                                                                value={props.absentState[`${employee.id}-${currentDate.toLocaleDateString('de-DE', DATE_OPTIONS)}`]?.comment || "Abwesend"} 
                                                                onChange={(event) => handleSelectChange(employee.id, currentDate, schedule?.id, event)}
                                                            >
                                                                {ABSENT_REASONS.map(reason => (
                                                                    <MenuItem key={reason} value={reason}>{reason}</MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    }
                                                    
                                                </div>   
                                            </td>
                                        );
                                    } else {
                                        return (
                                            <td key={colIndex} >
                                                <DateRangePicker                                                     
                                                    format="HH:mm" 
                                                    character="-" 
                                                    placeholder={isAbsent ? props.absentState[`${employee.id}-${currentDate.toLocaleDateString('de-DE', DATE_OPTIONS)}`]?.comment : (coreTime ? "Verboten" : "Erlaubt")} 
                                                    locale={de} 
                                                    caretAs={FaClock} 
                                                    oneTap 
                                                    onChange={value => handleLocalChange(employee.id, currentDate.toLocaleDateString('de-DE', DATE_OPTIONS), value, false, false, "")}                                                     
                                                    className="empty-cell" 
                                                    disabled={isDatePickerDisabled(employee.id, currentDate.toLocaleDateString('de-DE', DATE_OPTIONS))}
                                                />
                                                <div className="abwesend-checkbox">
                                                    <Tooltip title="Abwesendheit einstellen">
                                                        <FormControlLabel                                                             
                                                            control={                                                    
                                                                <Checkbox 
                                                                    defaultChecked={props.absentState[`${employee.id}-${currentDate.toLocaleDateString('de-DE', DATE_OPTIONS)}`]?.absent}
                                                                    onChange={(event) => handleCheckboxChange(employee.id, currentDate, schedule?.id, event)}
                                                            />} 
                                                            label="Abwesend"
                                                        />
                                                    </Tooltip>
                                                    {
                                                        props.absentState[`${employee.id}-${currentDate.toLocaleDateString('de-DE', DATE_OPTIONS)}`]?.absent && 
                                                        <FormControl fullWidth>
                                                            <InputLabel>Grund</InputLabel>
                                                            <Select 
                                                                label="Grund" 
                                                                value={props.absentState[`${employee.id}-${currentDate.toLocaleDateString('de-DE', DATE_OPTIONS)}`]?.comment || "Abwesend"} 
                                                                onChange={(event) => handleSelectChange(employee.id, currentDate, schedule?.id, event)}
                                                            >
                                                                <MenuItem value={"Abwesend"}>Abwesend</MenuItem>
                                                                <MenuItem value={"Urlaub"}>Urlaub</MenuItem>
                                                                <MenuItem value={"Berufsschule"}>Berufsschule</MenuItem>
                                                                <MenuItem value={"Frei"}>Frei</MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                    }
                                                </div>                                               
                                            </td>
                                        );
                                    }
                                })}
                                <td className="worktime-table-weeklytotal">
                                    {Math.floor(weeklyTotals[rowIndex] / 60)} Std {weeklyTotals[rowIndex] % 60} min
                                    {targetTimeObject && targetTimeObjectMonth && objectInMinutes > 0 && (
                                        <div className="weekly-total-time">
                                            <Tooltip title="wöchentliche Stundendifferenz">
                                                <span>
                                                    Differenz: {formatNegativeZero((Math.round(objectInMinutes) - weeklyTotals[rowIndex]) / 60)} Std {Math.abs((Math.round(objectInMinutes) - weeklyTotals[rowIndex]) % 60)} min
                                                </span>
                                            </Tooltip>
                                        </div>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                    <tr className="worktime-table-dailytotal" key={"lastRow"}>
                        <th>
                            <Tooltip title="Summe Tagesstunden minus Automatische Pause">
                                <span>Summe Tagesstunden</span>
                            </Tooltip>
                        </th>
                        {totalDurations.map((object, index) => {
                            return (
                                <td key={index}>
                                    {Math.floor(object /60)} Std {object % 60} min
                                </td>
                            )
                        })}
                        <th>
                            Insgesamt Woche: 
                            <br/>
                            {Math.floor(totalDurations.reduce((acc, curr) => acc + curr, 0) /60)} Std {totalDurations.reduce((acc, curr) => acc + curr, 0) % 60} min
                        </th>
                    </tr>
                </tbody>
            </>
        )

        return dynamicTable;
    }

    function createTableMain() {
        // Don't render table until all data is loaded
        if (props.isLoading) {
            return <LoadingSpinner />;
        }

        
        let dates = [];
        let startDate = props.currentDate;
        let weeklyTotals = [];
        
        for (let i = 0; i < 7; i++) {
            let date = new Date(startDate);
            date.setDate(startDate.getDate() + i);
            dates.push(date);
        }

        props.employees.forEach((employee) => {
            const coreTime = props.employeesCoreWorkTime.find(coreTime => coreTime.id === employee.assigned_core_working_hours_policy_id);
            let weeklyTotalMinutes = 0;
    
            dates.forEach((datesObject) => {
                let foundSchedule = false;
                let startTime = '';
                let endTime = '';
    
                const dateWeekDay = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"][datesObject.getDay()];
    
                if (coreTime) {
                    const period = coreTime.core_working_hours_periods.find(period => period.day_of_week === dateWeekDay);
                    if (period) {
                        foundSchedule = true;
                        startTime = period.start_time.slice(0, 5);
                        endTime = period.end_time.slice(0, 5);
                    }
                }
                
                const schedule = props.employeesSchedulingTime.find(schedule => schedule.employee_id === employee.id && new Date(schedule.date).toLocaleDateString('de-DE', DATE_OPTIONS) === datesObject.toLocaleDateString('de-DE', DATE_OPTIONS));
    
                if (schedule && !schedule.absent) {
                    foundSchedule = true;
                    startTime = schedule.start_time.slice(0, 5);
                    endTime = schedule.end_time.slice(0, 5);
                }

                if(coreTime && schedule && schedule.absent){
                    startTime = "00:00";
                    endTime = "00:00";
                }
    
                if (foundSchedule) {
                    let initialStartDate = new Date(datesObject.setHours(parseInt(startTime.slice(0, 2)), parseInt(startTime.slice(3, 5)), 0, 0));
                    let initialEndDate = new Date(datesObject.setHours(parseInt(endTime.slice(0, 2)), parseInt(endTime.slice(3, 5)), 0, 0));
    
                    const duration = ((initialEndDate - initialStartDate) / (1000 * 60)); // calculate duration in minutes

                    const durationMinusPause = duration - props.getAllTimePolicy[Math.min(Math.floor(duration / 60), 15)]
                    
                    weeklyTotalMinutes += durationMinusPause; // Add the duration to the weekly total
                }
            });
    
            weeklyTotals.push(weeklyTotalMinutes); // Store the weekly total for this employee
        });

        

        return <>
            <table className="work-time-planning-table">
                {tableFunction(dates, weeklyTotals)}
            </table>
        </>
            
    }

    return props.isLoading ?
        <LoadingSpinner /> :
        createTableMain()
}

export default PlanningTable;